import { ClientConfiguration } from '../types/AuthTypes';

/**
 * ConfigService
 * 
 * Manages fetching and caching of client configuration.
 * - Fetches fresh config on new tab/window (session start)
 * - Caches in sessionStorage for duration of session
 * - Public endpoint, no authentication required
 */
export class ConfigService {
  private static readonly SESSION_KEY = 'client-configuration';
  private static readonly SESSION_INIT_KEY = 'session-initialized';
  private static config: ClientConfiguration | null = null;

  /**
   * Get client configuration with session-aware caching
   */
  static async getConfig(): Promise<ClientConfiguration> {
    // Check for force reload parameter
    const urlParams = new URLSearchParams(window.location.search);
    const forceReload = urlParams.has('reloadConfig') || urlParams.has('forceConfigRefresh');
    
    if (forceReload) {
      console.log('Force reloading client configuration due to URL parameter');
      this.clearConfig();
      // Remove the parameter from URL to prevent repeated reloads
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('reloadConfig');
      newUrl.searchParams.delete('forceConfigRefresh');
      window.history.replaceState({}, '', newUrl.toString());
    }
    // Check if this is a fresh session (tab just opened)
    const isNewSession = !sessionStorage.getItem(this.SESSION_INIT_KEY);
    
    if (isNewSession) {
      // Fresh tab/window - always fetch
      sessionStorage.setItem(this.SESSION_INIT_KEY, 'true');
      return await this.fetchAndCache();
    }

    // Check memory cache
    if (this.config !== null) {
      return this.config;
    }

    // Check sessionStorage cache
    const cached = sessionStorage.getItem(this.SESSION_KEY);
    if (cached) {
      try {
        this.config = JSON.parse(cached);
        if (this.config !== null) {
          console.log('Loaded client configuration from sessionStorage:', this.config);
          return this.config;
        }
      } catch (error) {
        console.warn('Failed to parse cached config, fetching fresh', error);
      }
    }

    // Fetch as fallback
    return await this.fetchAndCache();
  }

  /**
   * Fetch configuration from API and cache it
   */
  private static async fetchAndCache(): Promise<ClientConfiguration> {
    try {
      // Use window.apiEndpoint if available, otherwise default
      const baseUrl = window.apiEndpoint || 'http://localhost:8080';
      const response = await fetch(`${baseUrl}/organization/client-configuration`);
      
      if (!response.ok) {
        throw new Error(`Config fetch failed: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      
      // Transform API response to ClientConfiguration format
      this.config = this.transformConfig(data);
      console.log('Fetched client configuration:', this.config);
      
      sessionStorage.setItem(this.SESSION_KEY, JSON.stringify(this.config));
      return this.config;
    } catch (error) {
      console.error('Failed to fetch client configuration:', error);
      throw error;
    }
  }

  /**
   * Transform API response to expected format
   */
  private static transformConfig(data: any): ClientConfiguration {
    // Helper function to extract tenant domain from scopes
    const extractTenantDomain = (scopes: string): string => {
      if (!scopes) return '';
      // Look for pattern like https://domain.onmicrosoft.com/api/api.access
      const match = scopes.match(new RegExp('https://([^/]+)/'));
      return match ? match[1] : '';
    };


    // Handle clientId - API sometimes returns customDomain instead of actual client ID
    let clientId = window.clientId || data.clientId || 'e753dc5f-27be-4642-b313-79ecafe4fd26';
    
    // If clientId looks like a URL (customDomain), use the fallback
    if (clientId.startsWith('https://')) {
      clientId = 'e753dc5f-27be-4642-b313-79ecafe4fd26';
    }

    // Merge API data with window fallbacks for missing fields
    return {
      adClientId: data.adClientId || window.adClientId || '',
      admin360Url: data.admin360Url || window.admin360Url || '',
      adScopes: data.adScopes || window.adScopes || '',
      adTenantId: data.adTenantId || window.adTenantId || '',
      adTenantName: data.adTenantName || window.adTenantName || '',
      apiClientId: data.apiClientId || window.apiClientId || '',
      apiEndpoint: data.apiEndpoint || window.apiEndpoint || '',
      apiRingCentralClientId: data.apiRingCentralClientId || window.apiRingCentralClientId || '',
      appInsightsConnectionString: data.appInsightsConnectionString || window.appInsightsConnectionString || '',
      authority: data.authority || window.authority || '',
      b2cPolicy: data.b2cPolicy || window.b2cPolicy || '',
      clientId: clientId,
      customDomain: data.customDomain || window.customDomain || '',
      domainHint: data.domainHint || window.domainHint || '',
      helixAppClientId: data.helixAppClientId || window.helixAppClientId || '',
      redirectUrl: data.redirectUrl || window.redirectUrl || '',
      scopes: data.scopes || window.scopes || '',
      tenantName: data.tenantName || window.tenantName,
      supportResponseTime: data.supportResponseTime || ''
    };
  }

  /**
   * Clear cached configuration (for logout or error recovery)
   */
  static clearConfig(): void {
    this.config = null;
    sessionStorage.removeItem(this.SESSION_KEY);
    sessionStorage.removeItem(this.SESSION_INIT_KEY);
  }

  /**
   * For testing: manually set config
   */
  static setConfigForTesting(config: ClientConfiguration): void {
    this.config = config;
    sessionStorage.setItem(this.SESSION_KEY, JSON.stringify(config));
    sessionStorage.setItem(this.SESSION_INIT_KEY, 'true');
  }
}
